import React, { useMemo, useState } from "react";

import Footer from "./Components/Footer";
import WorkBlock from "./Components/WorkBlock";
import WorkModal from "./Components/WorkModal";
import { ALL_VIDEOS, VimeoItem } from "./data/videoData";

import "./Works.scss";

export type { VimeoItem };

type CategoryKey =
  | "ALL"
  | "SMART CITY"
  | "KPA"
  | "GEMINI"
  | "EWC / GEN.G"
  | "MOTION GRAPHICS"
  | "ARTWORK"
  | "ADVERTISEMENT"
  | "MUSIC VIDEO"
  | "GAME"
  | "ETC";

/**
 * Categorize a video by title using keyword rules.
 *
 * ORDER MATTERS: more specific project-based matches come first
 * to prevent collisions (e.g. Gemini before KPA, since a Gemini video
 * contained "수업" which falsely matched KPA).
 */
function categorize(title: string): Exclude<CategoryKey, "ALL"> {
  const t = title;
  const lo = t.toLowerCase();

  // ── 1. Project-specific groups (most specific → first) ────────────

  // Smart City project (4 videos)
  if (lo.includes("smartcity") || lo.includes("smart_city") || lo.includes("smart city"))
    return "SMART CITY";

  // Gemini / Google campaign (3 videos) — MUST come before KPA
  // because one Gemini video contains "수업" which would match KPA
  if (lo.includes("gemini") || t.includes("젬스터"))
    return "GEMINI";

  // KPA educational series (21 videos)
  if (
    lo.includes("kpa") ||
    (lo.includes("lesson") && !lo.includes("blade")) ||
    t.includes("사전테스트") ||
    t.includes("점심시간") ||
    t.includes("쉬는시간") ||
    t.includes("교육종료")
  )
    return "KPA";

  // EWC / Gen.G esports content (31 videos)
  if (
    lo.includes("ewc") ||
    t.includes("젠랑") ||
    lo.includes("genrang") ||
    lo.includes("gen.g") ||
    lo.includes("geng") ||
    t.includes("젠지") ||
    t.includes("인스파이어") ||
    lo.includes("tigernation") ||
    (lo.includes("ggx") && !lo.includes("galaxy"))
  )
    return "EWC / GEN.G";

  // ── 2. Content-type categories (Vimeo showcase aligned) ───────────

  // Music Videos — clear MV/M-V/M/V patterns
  if (
    /\bm[\-\/]?v\b/i.test(t) ||
    /\bMV\b/.test(t) ||
    lo.includes("dance cover") ||
    lo.includes("커버 댄스")
  )
    return "MUSIC VIDEO";

  // Game trailers & cinematics
  if (
    t.includes("메이플스토리") ||
    t.includes("음양사") ||
    lo.includes("blade and soul") ||
    lo.includes("lyn") ||
    t.includes("린]") ||
    t.includes("이카루스")
  )
    return "GAME";

  // Artwork — opening titles, brand stories, title sequences
  if (
    t.includes("오프닝 타이틀") ||
    lo.includes("opening title") ||
    (lo.includes("title") && (t.includes("Kingdom") || t.includes("고요의 바다"))) ||
    lo.includes("brand stor") || lo.includes("brand_stor") ||
    t.includes("브랜드스토리") ||
    t.includes("나의 나라")
  )
    return "ARTWORK";

  // Motion Graphics — showreels, signature pieces, tech/industrial
  if (
    lo.includes("h-port") ||
    lo.includes("showreel") ||
    lo.includes("signature") ||
    lo.includes("toast_motion") ||
    lo.includes("reels") ||
    lo.includes("[scs]") ||
    lo.includes("[nuna]") ||
    lo.includes("hyundai card dive")
  )
    return "MOTION GRAPHICS";

  // Advertisement — brand commercials, CF, corporate videos
  // Matches Korean brand names and common ad patterns
  if (
    t.includes("삼성") || lo.includes("samsung") ||
    t.includes("롯데") ||
    t.includes("현대") || lo.includes("hyundai") ||
    lo.includes("sk ") || lo.includes("sk holdings") ||
    t.includes("교촌") ||
    t.includes("KT |") || t.includes("KT|") ||
    t.includes("기아") ||
    lo.includes("ssf shop") ||
    t.includes("네이버") ||
    t.includes("NH농협") ||
    lo.includes("not dead yet") ||
    t.includes("동아오츠카") ||
    t.includes("다우니") ||
    t.includes("맥심") ||
    t.includes("제일파프") ||
    t.includes("아웃백") ||
    t.includes("불스원") ||
    t.includes("락피도") ||
    t.includes("스타벅스") ||
    t.includes("정관장") ||
    t.includes("제주맥주") ||
    t.includes("르꼬끄") ||
    t.includes("리디북스") ||
    t.includes("하우빌드") ||
    t.includes("삼성화재") ||
    t.includes("강원 랩소디") ||
    t.includes("매드포갈릭") || lo.includes("mad for garlic") ||
    lo.includes("galaxy") ||
    lo.includes("hd현대") ||
    t.includes("현대해상") ||
    lo.includes("tvcf")
  )
    return "ADVERTISEMENT";

  return "ETC";
}

const ALL_CATEGORIES: CategoryKey[] = [
  "ALL",
  "EWC / GEN.G",
  "ADVERTISEMENT",
  "MUSIC VIDEO",
  "GAME",
  "ARTWORK",
  "MOTION GRAPHICS",
  "SMART CITY",
  "GEMINI",
  "ETC",
];
const PER_PAGE = 16;

// KPA 교육 시리즈 영상은 포트폴리오에서 제외
const VIDEOS = ALL_VIDEOS.filter((v) => categorize(v.title) !== "KPA");

function Works() {
  const [showModal, setShowModal] = useState(false);
  const [showItem, setShowItem] = useState<VimeoItem | null>(null);
  const [activeCategory, setActiveCategory] = useState<CategoryKey>("ALL");
  const [currentPage, setCurrentPage] = useState(1);

  const filtered = useMemo(() => {
    if (activeCategory === "ALL") return VIDEOS;
    return VIDEOS.filter((v) => categorize(v.title) === activeCategory);
  }, [activeCategory]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    ALL_CATEGORIES.forEach((c) => (counts[c] = 0));
    VIDEOS.forEach((v) => { counts[categorize(v.title)]++; });
    counts["ALL"] = VIDEOS.length;
    return counts;
  }, []);

  const handleCategoryChange = (cat: CategoryKey) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  return (
    <div className={showModal ? "bg-modal works-page-wr" : "works-page-wr"}>
      <div className='works-page container'>
        <div className='works-title-row'>
          <p className='works-title'>PORTFOLIO</p>
          <span className='works-loading'>{VIDEOS.length} VIDEOS</span>
        </div>

        <div className='works-filter'>
          {ALL_CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`works-filter__btn ${activeCategory === cat ? "active" : ""}`}
              onClick={() => handleCategoryChange(cat)}
            >
              {cat}
              <span className='works-filter__count'>{categoryCounts[cat] || 0}</span>
            </button>
          ))}
        </div>

        <WorkBlock
          workList={paged}
          setShowModal={setShowModal}
          setShowItem={(item: VimeoItem) => setShowItem(item)}
        />

        {totalPages > 1 && (
          <div className='works-pagination'>
            <button
              className='works-pagination__btn'
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              PREV
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
              <button
                key={num}
                className={`works-pagination__num ${currentPage === num ? "active" : ""}`}
                onClick={() => setCurrentPage(num)}
              >
                {num}
              </button>
            ))}
            <button
              className='works-pagination__btn'
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              NEXT
            </button>
          </div>
        )}

        <WorkModal
          showModal={showModal}
          setShowModal={setShowModal}
          item={showItem}
        />
      </div>
      <Footer />
    </div>
  );
}

export default Works;
